import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';
import {
  House,
  Package,
  ArrowsDownUp,
  Truck,
  CookingPot,
  ChartLine,
  ShoppingCart,
  SignOut,
  List,
  X,
  Users,
  ClipboardText,
  Lightbulb,
  Handshake,
  FileText,
  Lightning,
  ForkKnife,
  CalendarCheck,
  UserList,
  Wallet,
  Knife,
  ChartBar,
  CreditCard,
  Receipt,
  Bank,
  Carrot,
  ChartLineUp,
  Cow,
  Cube,
  Globe,
  CaretDown,
  CaretRight,
  Storefront,
  Money,
  Notebook,
  Gear,
  Vault,
  Trash,
} from '@phosphor-icons/react';

export function Layout({ children }) {
  const { user, logout, canAccessModule, hasPermission } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['stok', 'satin-alma', 'finans']);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Gruplu navigasyon yapısı
  const navigationGroups = [
    {
      id: 'ana',
      name: language === 'tr' ? 'Ana Sayfa' : 'Dashboard',
      icon: House,
      href: '/',
      items: []
    },
    {
      id: 'analiz',
      name: language === 'tr' ? 'Akıllı Analiz' : 'Smart Analytics',
      icon: Lightbulb,
      items: [
        { name: language === 'tr' ? 'Dönem Karşılaştırma' : 'Period Comparison', href: '/smart-analytics', icon: ChartLine, explicitPermission: 'smart_analytics' },
        { name: 'GOMŞU 360°', href: '/gomsu-360', icon: Lightbulb, explicitPermission: 'smart_analytics' },
      ]
    },
    {
      id: 'stok',
      name: language === 'tr' ? 'Stok Yönetimi' : 'Stock Management',
      icon: Package,
      items: [
        { name: t('nav.products'), href: '/products', icon: Package },
        { name: t('nav.stockOperations'), href: '/stock', icon: ArrowsDownUp },
        { name: t('nav.stockReport'), href: '/stock-report', icon: ChartBar },
        { name: t('nav.inventoryCount'), href: '/inventory-count', icon: ClipboardText, managerOnly: true },
        { name: t('nav.prepKitchen'), href: '/processing', icon: Knife, managerOnly: true },
        { name: language === 'tr' ? 'Zayi / Fire Takibi' : 'Waste Tracking', href: '/waste-tracking', icon: Trash, managerOnly: true },
      ]
    },
    {
      id: 'sevkiyat',
      name: language === 'tr' ? 'Sevkiyat' : 'Transfer',
      icon: Truck,
      items: [
        { name: t('nav.transfer'), href: '/transfer', icon: Truck },
        { name: t('nav.transferInvoice'), href: '/transfer-requests', icon: FileText },
        { name: t('nav.transferCost'), href: '/transfer-cost-report', icon: ChartLine, managerOnly: true },
      ]
    },
    {
      id: 'satin-alma',
      name: language === 'tr' ? 'Satın Alma' : 'Purchasing',
      icon: Storefront,
      items: [
        { name: t('nav.vegetables'), href: '/vegetables', icon: Carrot, managerOnly: true },
        { name: t('nav.meat'), href: '/meat', icon: Cow, managerOnly: true },
        { name: t('nav.generalProducts'), href: '/general-products', icon: Cube, managerOnly: true },
        { name: t('nav.suppliers'), href: '/suppliers', icon: Handshake, managerOnly: true },
        { name: t('nav.orderSuggestion'), href: '/auto-orders', icon: Lightning, managerOnly: true },
      ]
    },
    {
      id: 'satis',
      name: language === 'tr' ? 'Satış & Mutfak' : 'Sales & Kitchen',
      icon: CookingPot,
      items: [
        { name: t('nav.sales'), href: '/sales', icon: ShoppingCart },
        { name: t('nav.recipes'), href: '/recipes', icon: CookingPot, managerOnly: true },
        { name: t('nav.staffMeals'), href: '/staff-meals', icon: ForkKnife },
        { name: t('nav.mealPlanning'), href: '/meal-planning', icon: CalendarCheck, managerOnly: true },
        { name: t('nav.smartMenu'), href: '/smart-menu', icon: Lightbulb, managerOnly: true },
      ]
    },
    {
      id: 'finans',
      name: language === 'tr' ? 'Finans & Muhasebe' : 'Finance & Accounting',
      icon: Money,
      adminOnly: true,
      items: [
        { name: language === 'tr' ? 'Kasa Yönetimi' : 'Cash Register', href: '/cash-register', icon: Vault, managerOnly: true },
        { name: t('nav.expenses'), href: '/expenses', icon: Wallet, adminOnly: true },
        { name: t('nav.profitLoss'), href: '/profit-loss', icon: ChartLineUp, adminOnly: true },
        { name: language === 'tr' ? 'Aylık Özet Rapor' : 'Monthly Summary', href: '/monthly-report', icon: FileText, adminOnly: true },
        { name: t('nav.paymentReport'), href: '/payment-report', icon: CreditCard, adminOnly: true },
        { name: t('nav.checkManagement'), href: '/checks', icon: Receipt, adminOnly: true },
        { name: t('nav.loanManagement'), href: '/loans', icon: Bank, adminOnly: true },
      ]
    },
    {
      id: 'yonetim',
      name: language === 'tr' ? 'Yönetim' : 'Management',
      icon: Gear,
      adminOnly: true,
      items: [
        { name: t('nav.staff'), href: '/staff', icon: UserList, adminOnly: true },
        { name: t('nav.users'), href: '/users', icon: Users, adminOnly: true },
        { name: language === 'tr' ? 'Kullanıcı Yetkileri' : 'User Permissions', href: '/users/permissions', icon: Gear, adminOnly: true },
        { name: t('nav.reports'), href: '/reports', icon: ChartLine, managerOnly: true },
      ]
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Module key'leri href'den çıkar (örn: /recipes -> recipes, /waste-tracking -> waste_tracking)
  const getModuleKey = (href) => {
    if (!href) return null;
    const key = href.replace('/', '').replace(/-/g, '_');
    // Özel mapping'ler
    const moduleMap = {
      '': 'dashboard',
      'stock': 'stock',
      'stock_report': 'stock_report',
      'inventory_count': 'inventory_count',
      'processing': 'processing',
      'waste_tracking': 'waste_tracking',
      'transfer': 'transfer',
      'transfer_requests': 'transfer_requests',
      'transfer_cost_report': 'transfer_cost_report',
      'vegetables': 'vegetables',
      'meat': 'meat',
      'general_products': 'general_products',
      'suppliers': 'suppliers',
      'auto_orders': 'auto_orders',
      'products': 'products',
      'sales': 'sales',
      'recipes': 'recipes',
      'staff_meals': 'staff_meals',
      'meal_planning': 'meal_planning',
      'smart_menu': 'smart_menu',
      'cash_register': 'cash_register',
      'expenses': 'expenses',
      'profit_loss': 'profit_loss',
      'monthly_report': 'monthly_report',
      'payment_report': 'payment_report',
      'checks': 'checks',
      'loans': 'loans',
      'staff': 'staff',
      'users': 'users',
      'users/permissions': 'user_permissions',
      'reports': 'reports',
      'smart_analytics': 'smart_analytics',
    };
    return moduleMap[key] || key;
  };

  const isItemVisible = (item) => {
    // Önce rol bazlı kontrol (eski sistem - geriye uyumluluk)
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.managerOnly && user?.role === 'staff') return false;
    
    // Sonra permission bazlı kontrol (yeni sistem)
    if (item.explicitPermission && !hasPermission(item.explicitPermission, 'view')) return false;
    const moduleKey = getModuleKey(item.href);
    if (moduleKey && !canAccessModule(moduleKey)) return false;
    
    return true;
  };

  const isGroupVisible = (group) => {
    if (group.adminOnly && user?.role !== 'admin') return false;
    if (group.managerOnly && user?.role === 'staff') return false;
    // Gruptaki en az bir öğe görünür olmalı
    if (group.items.length > 0) {
      return group.items.some(item => isItemVisible(item));
    }
    return true;
  };

  const isGroupActive = (group) => {
    if (group.href) return location.pathname === group.href;
    return group.items.some(item => location.pathname === item.href);
  };

  return (
    <div className="noise-bg min-h-screen">
      <div className="app-layout">
        {/* Mobile menu button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-40 p-2 rounded-lg bg-card border border-border shadow-sm"
          data-testid="mobile-menu-button"
        >
          <List size={24} weight="bold" />
        </button>

        {/* Sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            'sidebar-floating bg-card border border-border flex flex-col',
            'fixed lg:sticky top-0 z-50',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
            'transition-transform duration-300 ease-out'
          )}
          data-testid="sidebar"
        >
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-xl font-bold tracking-tight text-foreground">
                  Gomsu Restoran
                </h1>
                <p className="text-xs text-muted-foreground mt-1 tracking-wide uppercase">
                  {language === 'tr' ? 'Stok Yönetim Sistemi' : 'Stock Management'}
                </p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-muted"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navigationGroups.map((group) => {
              if (!isGroupVisible(group)) return null;
              
              const GroupIcon = group.icon;
              const isExpanded = expandedGroups.includes(group.id);
              const hasItems = group.items.length > 0;
              const groupActive = isGroupActive(group);

              // Tek sayfa grubu (Ana Sayfa gibi)
              if (group.href) {
                const isActive = location.pathname === group.href;
                return (
                  <Link
                    key={group.id}
                    to={group.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    data-testid={`nav-${group.id}`}
                  >
                    <GroupIcon size={20} weight={isActive ? 'fill' : 'duotone'} />
                    {group.name}
                  </Link>
                );
              }

              // Gruplu menü
              return (
                <div key={group.id} className="space-y-1">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium',
                      'transition-colors duration-200',
                      groupActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    data-testid={`nav-group-${group.id}`}
                  >
                    <GroupIcon size={20} weight={groupActive ? 'fill' : 'duotone'} />
                    <span className="flex-1 text-left">{group.name}</span>
                    {hasItems && (
                      isExpanded 
                        ? <CaretDown size={16} weight="bold" />
                        : <CaretRight size={16} weight="bold" />
                    )}
                  </button>
                  
                  {/* Alt menü öğeleri */}
                  {hasItems && isExpanded && (
                    <div className="ml-4 pl-4 border-l border-border space-y-1">
                      {group.items.map((item) => {
                        if (!isItemVisible(item)) return null;
                        
                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;
                        
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm',
                              'transition-colors duration-200',
                              isActive
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                            data-testid={`nav-${item.href.slice(1) || 'home'}`}
                          >
                            <Icon size={18} weight={isActive ? 'fill' : 'duotone'} />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200 mb-2"
              data-testid="language-toggle"
            >
              <Globe size={20} weight="duotone" />
              <span className="flex-1 text-left">{t('language.title')}</span>
              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                {language === 'tr' ? 'TR' : 'EN'}
              </span>
            </button>
            
            <div className="flex items-center gap-3 px-4 py-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-2 flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
              data-testid="logout-button"
            >
              <SignOut size={20} weight="duotone" />
              {t('nav.logout')}
            </button>
            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-border text-center text-[10px] text-muted-foreground/60">
              © 2026 Sergen Sınar
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="p-4 lg:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
